import i18n from '../../../i18n/config';
import { useTranslation } from 'react-i18next';
// arsenal-tpms/frontend/src/components/schematics/VehicleTelemetryModal/TableTab.jsx
import React, { useCallback, useMemo } from 'react';
import {
    Box,
    Typography,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Paper
} from '@mui/material';
import {
    Refresh as RefreshIcon,
    GetApp as ExportIcon
} from '@mui/icons-material';

import {
    getAvailablePositions,
    convertTelemetryValue,
    formatValue,
    getMetricUnit,
    unitConversionUtils as createUnitConversionUtils
} from './utils';

const TableTab = ({
    vehicle,
    telemetryData,
    telemetryLoading,
    refetchTelemetry,
    unitPreferences,
    formatDateTimeLocal,
    timezoneInfo
}) => {
  const { t } = useTranslation();
    // Estados para tabla
    const [tablePage, setTablePage] = React.useState(0);
    const [tableRowsPerPage, setTableRowsPerPage] = React.useState(25);

    // Unit conversion utilities
    const unitConversionUtils = useMemo(
        () => createUnitConversionUtils(unitPreferences),
        [unitPreferences]
    );

    // Process chart data para cada métrica
    const processChartData = useCallback((metric) => {
        if (!telemetryData || !Array.isArray(telemetryData)) {
            return { series: [] };
        }

        let filteredData = telemetryData;

        // Filtrar por tipo de dato según métrica
        if (metric === 'acceleration') {
            filteredData = telemetryData.filter(item => item.type === 'ACC');
        } else {
            filteredData = telemetryData.filter(item => item.type === 'PSI');
        }

        const dataByPosition = new Map();

        filteredData.forEach(item => {
            const actualSensorId = item.raw_payload?.id?.toString() || item.sensor_id;
            const sensorAssignment = vehicle.vehicle_sensors?.find(vs => vs.sensor_id.toString() === actualSensorId.toString());

            if (!sensorAssignment) return;

            const position = sensorAssignment.position;
            if (!dataByPosition.has(position)) {
                dataByPosition.set(position, []);
            }

            const rawTimestamp = item.time_stamp || item.created_at;
            let timestamp;

            if (typeof rawTimestamp === 'string') {
                if (!rawTimestamp.includes('Z') && !rawTimestamp.includes('+') && !rawTimestamp.includes('-', 10)) {
                    const utcTimestamp = rawTimestamp.includes('T') ? rawTimestamp + 'Z' : rawTimestamp + 'T00:00:00Z';
                    timestamp = new Date(utcTimestamp).getTime();
                } else {
                    timestamp = new Date(rawTimestamp).getTime();
                }
            } else {
                timestamp = new Date(rawTimestamp).getTime();
            }

            const convertedValue = convertTelemetryValue(item, metric, unitPreferences, unitConversionUtils);

            if (convertedValue !== null) {
                dataByPosition.get(position).push([timestamp, convertedValue]);
            }
        });

        const series = Array.from(dataByPosition.entries())
            .sort((a, b) => a[0] - b[0])
            .map(([position, data]) => {
                data.sort((a, b) => a[0] - b[0]);
                return {
                    name: `${position}`,
                    data: data
                };
            });

        return { series };
    }, [telemetryData, vehicle?.vehicle_sensors, unitPreferences, unitConversionUtils]);

    // Generate table data
    const generateTableData = useCallback(() => {
        const allChartData = {};
        const metrics = ['pressure', 'temperature', t('hardcoded.components_schematics_vehicletelemetrymodal.acceleration')];

        metrics.forEach(metric => {
            const chartData = processChartData(metric);
            allChartData[metric] = chartData.series;
        });

        const combinedData = [];
        const timestampsSet = new Set();

        metrics.forEach(metric => {
            allChartData[metric].forEach(series => {
                series.data.forEach(point => timestampsSet.add(point[0]));
            });
        });

        const sortedTimestamps = Array.from(timestampsSet).sort((a, b) => a - b);
        const availablePositions = getAvailablePositions(vehicle);

        sortedTimestamps.forEach(timestamp => {
            const row = {
                timestamp: new Date(timestamp),
                dateTime: formatDateTimeLocal(timestamp)
            };

            availablePositions.forEach(position => {
                metrics.forEach(metric => {
                    const key = t('hardcoded.components_schematics_vehicletelemetrymodal.metric_position_position', { metric: metric, position: position });
                    let value = null;

                    const series = allChartData[metric].find(s => s.name === `${position}`);
                    if (series) {
                        const dataPoint = series.data.find(point => point[0] === timestamp);
                        if (dataPoint) value = dataPoint[1];
                    }

                    row[key] = value;
                });
            });

            combinedData.push(row);
        });

        return combinedData;
    }, [processChartData, vehicle, formatDateTimeLocal]);

    // Table data
    const tableData = useMemo(() => generateTableData(), [generateTableData]);
    const availablePositions = useMemo(() => getAvailablePositions(vehicle), [vehicle]);

    // Pagination
    const startIndex = tablePage * tableRowsPerPage;
    const endIndex = startIndex + tableRowsPerPage;
    const paginatedData = tableData.slice(startIndex, endIndex);

    return (
        <Box sx={{ p: { xs: 1, sm: 3 }, height: '100%', overflowY: 'auto' }}>
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <Box>
                    <Typography variant="h6">{t('hardcoded.components_schematics_vehicletelemetrymodal_tabletab.datos_de_telemetria_-_vista')}</Typography>
                    {timezoneInfo && (
                        <Typography variant="caption" color="text.secondary">
                            {t('hardcoded.components_schematics_vehicletelemetrymodal_comparisontab.zona_horaria')}{timezoneInfo.timezone}
                        </Typography>
                    )}
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <Button startIcon={<ExportIcon />} variant="outlined" size="small">
                        {i18n.t('audit.export.csv')}</Button>
                    <Button startIcon={<RefreshIcon />} variant="outlined" size="small" onClick={refetchTelemetry}>
                        {i18n.t('audit.actions.UPDATE')}</Button>
                </Box>
            </Box>

            <TableContainer component={Paper} sx={{ maxHeight: { xs: 'calc(100vh - 280px)', sm: 600 }, overflowX: 'auto' }}>
                <Table stickyHeader size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{
                                fontWeight: 'bold',
                                bgcolor: 'error.main',
                                color: 'white',
                                minWidth: { xs: 130, sm: 250 }
                            }}>
                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                    {i18n.t('hardcoded.components_schematics_vehicletelemetrymodal.fecha_y_hora')}</Typography>
                            </TableCell>
                            {availablePositions.map(position => (
                                <TableCell key={`pressure-${position}`} align="center" sx={{
                                    fontWeight: 'bold',
                                    bgcolor: 'primary.main',
                                    color: 'white'
                                }}>
                                    {i18n.t('hardcoded.components_schematics_vehicletelemetrymodal.pos')}{position}<br />
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                                        {t('hardcoded.components_schematics_vehicletelemetrymodal_tabletab.presion')}{getMetricUnit('pressure', unitPreferences)})
                                    </Typography>
                                </TableCell>
                            ))}
                            {availablePositions.map(position => (
                                <TableCell key={`temp-${position}`} align="center" sx={{
                                    fontWeight: 'bold',
                                    bgcolor: 'grey.700',
                                    color: 'white'
                                }}>
                                    {i18n.t('hardcoded.components_schematics_vehicletelemetrymodal.pos')}{position}<br />
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                                        {i18n.t('hardcoded.components_schematics_vehicletelemetrymodal_tabletab.temp')}{getMetricUnit('temperature', unitPreferences)})
                                    </Typography>
                                </TableCell>
                            ))}
                            {availablePositions.map(position => (
                                <TableCell key={t('hardcoded.components_schematics_vehicletelemetrymodal.acc-position', { position: position })} align="center" sx={{
                                    fontWeight: 'bold',
                                    bgcolor: 'info.main',
                                    color: 'white'
                                }}>
                                    {i18n.t('hardcoded.components_schematics_vehicletelemetrymodal.pos')}{position}<br />
                                    <Typography variant="caption" sx={{ fontSize: '0.65rem' }}>
                                        {i18n.t('hardcoded.components_schematics_vehicletelemetrymodal_tabletab.acel')}{getMetricUnit(t('hardcoded.components_schematics_vehicletelemetrymodal.acceleration'), unitPreferences)})
                                    </Typography>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {paginatedData.map((row, index) => (
                            <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                                <TableCell sx={{ fontWeight: 'medium', minWidth: 250, verticalAlign: 'top', py: 1.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.main' }}>
                                        {row.dateTime}
                                    </Typography>
                                </TableCell>
                                {availablePositions.map(position => (
                                    <TableCell key={`pressure-${position}`} align="center">
                                        {formatValue(row[t('hardcoded.components_schematics_vehicletelemetrymodal.pressure_position_position', { position: position })], 'pressure', unitPreferences)}
                                    </TableCell>
                                ))}
                                {availablePositions.map(position => (
                                    <TableCell key={`temp-${position}`} align="center">
                                        {formatValue(row[t('hardcoded.components_schematics_vehicletelemetrymodal.temperature_position_position', { position: position })], 'temperature', unitPreferences)}
                                    </TableCell>
                                ))}
                                {availablePositions.map(position => (
                                    <TableCell key={t('hardcoded.components_schematics_vehicletelemetrymodal.acc-position', { position: position })} align="center">
                                        {formatValue(row[t('hardcoded.components_schematics_vehicletelemetrymodal.acceleration_position_position', { position: position })], t('hardcoded.components_schematics_vehicletelemetrymodal.acceleration'), unitPreferences)}
                                    </TableCell>
                                ))}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            <TablePagination
                component="div"
                count={tableData.length}
                page={tablePage}
                onPageChange={(event, newPage) => setTablePage(newPage)}
                rowsPerPage={tableRowsPerPage}
                onRowsPerPageChange={(event) => {
                    setTableRowsPerPage(parseInt(event.target.value, 10));
                    setTablePage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                labelRowsPerPage={t('hardcoded.components_alarms_alarmlist.filas_por_pagina')}
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
            />
        </Box>
    );
};

export default TableTab;